/**
 * Cash Flow Forecast Chart Component
 * Phase 6 Batch 3 - EOS Add-on Reports
 *
 * 90-day cash flow forecast showing expected incoming payments and work
 */
import { useCashFlowForecast, type CashFlowForecastData } from '@/hooks/useDashboardReports';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertCircle, DollarSign } from 'lucide-react';

export function CashFlowForecastChart({ days = 90 }: { days?: number }) {
  const { data, isLoading, error } = useCashFlowForecast(days);

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load cash flow forecast data
      </div>
    );
  }

  const forecastData = data as { success: boolean } & CashFlowForecastData;
  const expectedIncoming = forecastData?.expected_incoming || [];
  const availableToBill = forecastData?.available_to_bill;
  const scheduledWork = forecastData?.scheduled_work || [];
  const summary = forecastData?.summary;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="cash-flow-forecast-chart">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <DollarSign size={20} />
          Cash Flow Forecast (Next {days} Days)
        </h2>
      </div>

      {/* Expected Incoming by Week */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Expected Incoming Payments by Week</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={expectedIncoming} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="0" stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="week_label"
              tick={{ fontSize: 12, fill: '#6b7280' }}
              angle={-45}
              textAnchor="end"
              height={80}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={{ stroke: '#e5e7eb' }}
            />
            <Tooltip
              formatter={(value: number) => formatCurrency(value)}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}
            />
            <Bar dataKey="due_amount" name="Expected" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Available to Bill */}
      {availableToBill && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Available to Invoice Now</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-2xl font-bold text-sky-700">
                {formatCurrency(availableToBill.total_amount)}
              </div>
              <div className="text-xs text-gray-600 mt-1">Ready to bill</div>
            </div>
            <div>
              <div className="text-xl font-semibold text-gray-700">
                {availableToBill.project_count}
              </div>
              <div className="text-xs text-gray-600 mt-1">Projects</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">If invoiced this week</div>
              <div className="text-lg font-semibold text-sky-600">
                {formatCurrency(availableToBill.if_invoiced_this_week.estimated_amount)}
              </div>
              <div className="text-xs text-gray-500">{availableToBill.if_invoiced_this_week.adds_to_month}</div>
            </div>
          </div>
        </div>
      )}

      {/* Scheduled Work by Month */}
      {scheduledWork.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Future Scheduled Work</h3>
          <div className="grid grid-cols-2 gap-3">
            {scheduledWork.slice(0, 4).map((month) => (
              <div key={month.month} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="text-xs text-gray-600 mb-1">{month.month_label}</div>
                <div className="text-lg font-semibold text-gray-900">
                  {formatCurrency(month.scheduled_amount)}
                </div>
                <div className="text-xs text-gray-500">{month.project_count} projects</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div className="mt-4 grid grid-cols-4 gap-4 pt-4 border-t border-gray-200">
          <div>
            <span className="text-gray-600 text-sm">Expected Incoming:</span>
            <div className="font-semibold text-lg text-emerald-600">
              {formatCurrency(summary.total_expected_incoming)}
            </div>
          </div>
          <div>
            <span className="text-gray-600 text-sm">Available to Bill:</span>
            <div className="font-semibold text-lg text-sky-600">
              {formatCurrency(summary.total_available_to_bill)}
            </div>
          </div>
          <div>
            <span className="text-gray-600 text-sm">Scheduled Work:</span>
            <div className="font-semibold text-lg text-slate-600">
              {formatCurrency(summary.total_scheduled_work)}
            </div>
          </div>
          <div>
            <span className="text-gray-600 text-sm">Net Forecast:</span>
            <div className={`font-semibold text-lg ${summary.net_forecast >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {formatCurrency(summary.net_forecast)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
